// Gezinsprofiel "Wat past bij ons gezin" (verzoek oprichter 2026-09-24; docs/DESIGN.md §8.P,
// docs/ONTWERP.md beslissingen 106 en 107, docs/PRIVACY-gezinsprofiel.md). De vraag begint
// herkenbaar ("Keuken en tradities": Nederlands, Turks, ...), maar gaat over GEWOONTES, niet over
// nationaliteit of afkomst: nationaliteit vragen of bewaren we nooit. Een traditie STELT alleen
// VOOR (geloof, eetregels, bijzondere dagen om zelf aan te zetten), neemt nooit iets aan en zet
// nooit iets aan. Alles is optioneel.
//
// Dit bestand is de woordenlijst (codes). Codes zijn Engels en stabiel (database, API, labels);
// namen en uitleg staan in packages/i18n (profileApp.nl.ts). Pure data, geen Zod: de webapp mag
// dit importeren via `@amumin/domain/profile`, de kiosk alleen als `import type`.
/**
 * Stap 0 "Keuken en tradities" (meerkeuze: een gezin kan Turks EN Nederlands zijn). ALLEEN VOOR DE
 * SESSIE (wizard of Instellingen): de keuze stuurt de voorstelkaart, de volgorde van de keuzes en
 * welke bijzondere dagen we aanbieden. We BEWAREN geen tradities (minimalisatie, AVG art. 5 lid 1
 * sub c): opgeslagen wordt alleen wat de app nodig heeft, de keukens die het gezin graag eet
 * (`household_profile.kitchens`, zie traditionKitchens). `other` = "Anders": opent de keuze
 * "Welke keukens eten jullie graag?" en stelt niets voor.
 */
export const TRADITIONS = [
    'dutch',
    'turkish',
    'moroccan',
    'surinamese',
    'antillean',
    'indonesian',
    'moluccan',
    'italian',
    'polish',
    'middle_eastern',
    'other',
];
/**
 * Stap 1 "Geloof". `other` = "Een ander geloof" (boeddhistisch, sikh, alevitisch, bahá'í, ...):
 * Bidden blijft zichtbaar, de opties van de genoemde geloven niet. "Geen geloof" is een lege lijst
 * (beantwoord), niet null (niet beantwoord). Later kan er een geloof bij zonder schemawijziging.
 */
export const FAITHS = ['islam', 'christianity', 'judaism', 'hinduism', 'other'];
/**
 * Golf 6b (oprichter 26-09: "een moslim kan ook niet praktiserend zijn, dus moet het optioneel
 * zijn"): gewoontes bij een geloof, elk los aan te vinken. Alleen wat het gezin aanvinkt, komt als
 * sjabloon, eerste taak, pictogramvoorstel of kioskoptie. De code is gelijk aan het pictogram-id.
 * Bidden hoort bij elk geloof (één code).
 */
export const PRACTICES = [
    'prayer',
    'wudu',
    'mosque',
    'quran',
    'church',
    'shabbat',
    'synagogue',
    'mandir',
];
/**
 * Golf 6c (oprichter 26-09, f2-14: "vraag bij de onboarding wat de mazhab is, zodat je veel van
 * deze zaken daaraan kunt hangen"): de mazhab (rechtsschool) van het gezin. Alleen bij Islam in het
 * gezinsprofiel; null of weg = "Weet ik niet / sla over". Een korte vaste waarde (CHECK in
 * 0045_mazhab), nooit vrije tekst. Wat eraan hangt (standaard voor de Asr-tijd, voorstel voor de
 * gebedskalender) staat in packages/domain/src/prayer/madhab.ts. We leiden de mazhab NOOIT af uit
 * een keuken of traditie: het gezin kiest hem zelf.
 */
export const MADHABS = ['hanafi', 'shafii', 'maliki', 'hanbali'];
/**
 * Stap 2 "Eten". Allergieën vallen nu buiten de scope; het model laat ze later toe als extra codes
 * (bijvoorbeeld `allergy_peanut`) met een eigen ingrediëntvlag, zonder schemawijziging (de
 * database controleert alleen de vorm van een code). Dan wel een nieuwe versie van de
 * toestemmingstekst: allergieën zijn gezondheidsgegevens (ook AVG art. 9).
 * `no_beef`: veel hindoeïstische gezinnen (in Nederland vaak Surinaams-Hindoestaans) eten wel
 * vlees, maar geen rundvlees.
 */
export const FOOD_RULES = [
    'halal',
    'no_pork',
    'no_beef',
    'vegetarian',
    'vegan',
    'kosher',
    'no_alcohol',
];
/**
 * Keuken van een gerecht (label op de receptencollectie, 0024) en wat een gezin graag eet
 * (`household_profile.kitchens`). De eerste tien hebben dezelfde code als een traditie; de rest zijn
 * keukens die in elk gezin op tafel komen, of die "Anders" kan kiezen. Regio's in plaats van een
 * werelddeel ("Centraal-Afrikaans", niet "Afrikaans"). Een keuken verandert alleen de VOLGORDE,
 * verbergt nooit iets: een Hollands gezin krijgt ook couscous met kip, een Turks gezin ook bami.
 */
export const KITCHENS = [
    'dutch',
    'turkish',
    'moroccan',
    'surinamese',
    'antillean',
    'indonesian',
    'moluccan',
    'italian',
    'polish',
    'middle_eastern',
    'chinese',
    'indian',
    'mexican',
    'greek',
    'west_african',
    'central_african',
    'east_african',
    'american',
];
/**
 * Wat een ingrediënt of gerecht bevat (afgeleid uit Nederlandse ingrediëntnamen, zie
 * ingredients.ts). `pork` en `beef` impliceren `meat`. `shellfish` = niet-koosjere zeevruchten
 * (ook paling en inktvis). `gelatin` staat apart: vaak van varken, soms van rund of vis.
 */
export const INGREDIENT_FLAGS = [
    'pork',
    'beef',
    'meat',
    'fish',
    'shellfish',
    'dairy',
    'egg',
    'honey',
    'gelatin',
    'alcohol',
];
/**
 * Bijzondere dagen die een ouder ZELF aanzet ("Bijzondere dagen in de agenda"; oprichter
 * 2026-09-24: "de culturele dagen niet automatisch toevoegen, dit moet wel een optie zijn"). Een
 * traditie of geloof BIEDT een set aan, zet hem nooit aan. De dagen per set staan in days.ts.
 */
export const DAY_SETS = [
    'tr_national',
    'keti_koti',
    'indies_remembrance',
    'id_independence',
    'it_national',
    'pl_national',
    'islamic',
    'christian',
    'jewish',
    'hindu',
];
/**
 * Versie van de toestemmingstekst (AVG art. 9 lid 2 sub a). Wijzigt de tekst in i18n
 * (`appText.profile.consent` of `appText.profile.selfConsent`), dan komt er een nieuwe versie met
 * een nieuwe hash in CONSENT_TEXT_HASHES (de test in apps/api herberekent hem, zodat een gewijzigde
 * tekst zonder nieuwe versie faalt). Wordt per toestemming opgeslagen (text_version).
 */
// 2026-09-25.3 (0027, release E): de korte punten van 2026-09-25.2 plus een neutraal punt dat sommige
// keuzes extra onderdelen op het bord geven, zoals gebedstijden, en dat iedereen in de kamer ze ziet
// (docs/PRIVACY-gezinsprofiel.md §11). Geen geloof wordt apart genoemd (DESIGN §8.GB.1). Een
// toestemming onder een oudere versie draagt geen module (0027 module_consent_versions_without).
// 2026-09-25.4 (review e r3): hetzelfde punt zonder voorbeeld ("extra onderdelen op het bord"), zodat
// geen gezin het woord gebedstijden leest. .3 en .4 dragen allebei de module.
export const PROFILE_CONSENT_VERSION = '2026-09-25.4';
/**
 * SHA-256 (hex) van de toestemmingsteksten per versie: nl.text + nl.check + nl.selfText +
 * nl.selfCheck + en.text + en.check + en.selfText + en.selfCheck, samengevoegd met '\n'. Oude
 * versies blijven staan (hun tekst staat in git): zo bewijst text_version wat de ouder las.
 */
export const CONSENT_TEXT_HASHES = {
    '2026-09-24.3': 'b52c143cf7bf655c2b2de15fb538c81301badaa2c3ac2fe033d8b126a6c5266e',
    // .4 (review d r1): de tekst in korte punten, met verwijzing naar de privacyverklaring.
    '2026-09-24.4': '935c82384c01641f185a626cfe34ee4d35c828bcdd6296c3c9adf71ce098525d',
    // 2026-09-25.1 (review d4-ux r1): de eigen toestemming zegt "Alleen jij en je ouders zien het",
    // zoals de pagina zelf (was "Alleen ouders zien het", terwijl de 16+ het zelf ook ziet).
    '2026-09-25.1': 'fa52f9c662e961088c5477169879fc261ceae5bfe0bd0d93f5f8d5d71cb9a243',
    // 2026-09-25.2 (review d4-ux r2): de eigen toestemming zegt "Alleen jij en de ouders in dit gezin
    // zien het"; klopt voor een volwassene (de andere ouder ziet het) en voor een kind van 16+.
    '2026-09-25.2': '4807483cf2d82c12a8f94e29cfa96432a282e8ada15072585800b892fd8bc684',
    // 2026-09-25.3 (0027, release E): 2026-09-25.2 plus het neutrale punt over extra onderdelen op het
    // bord (zoals gebedstijden).
    '2026-09-25.3': '500b8d848cea6128bc06efccbd48815384f6e9bee6020944178392d9b56b5ef4',
    // 2026-09-25.4 (review e r3): het punt over extra onderdelen noemt geen gebedstijden meer; een
    // gezin zonder geloofskeuze leest zo geen enkel religieus woord in de toestemming.
    '2026-09-25.4': '8f27ef39af855e3c97333e24640919662b5e92c02f846e92c47f90a69646dafe',
};
/**
 * Veldnamen van het profiel. Dit zijn de ENIGE waarden die in `audit_log.diff` mogen staan
 * (`{ fields: [...] }`, 0023 trigger `audit_log_profile_guard`) en de sleutels die pino redigeert.
 */
export const PROFILE_FIELDS = [
    'kitchens',
    'faiths',
    // Golf 6b (0041): aangevinkte gewoontes bij het geloof.
    'practices',
    // Golf 6c (0045): de mazhab (rechtsschool) bij Islam.
    'madhab',
    'food_rules',
    'food_rules_extra',
    'food_rules_skip',
    'day_sets',
    'day_keys_off',
    'ics_days',
    'suggestion_answered',
    'consent',
    'subjects',
    'eats_other',
    // Golf 2 (0030_snel): traditie en bevestigde dagen van de islamitische set.
    'day_calendar',
    'day_fixes',
];
/** Toegestane audit-acties voor het profiel (zelfde lijst als de trigger in 0023). */
export const PROFILE_AUDIT_ACTIONS = [
    'profile.update',
    'profile.member.update',
    'profile.member.reset',
    'profile.consent.give',
    'profile.consent.renew',
    'profile.consent.withdraw',
    'profile.subject.confirm',
    'profile.subject.withdraw',
    'profile.eats_other.set',
    'profile.eats_other.clear',
];
/** Vorm van een code in de database (0023_gezinsprofiel controleert dezelfde regex). */
export const PROFILE_CODE_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
const TRADITION_SET = toSet(TRADITIONS);
const FAITH_SET = toSet(FAITHS);
const PRACTICE_SET = toSet(PRACTICES);
const RULE_SET = toSet(FOOD_RULES);
const KITCHEN_SET = toSet(KITCHENS);
const DAY_SET_SET = toSet(DAY_SETS);
export function isTradition(v) {
    return TRADITION_SET[v] === true;
}
export function isFaith(v) {
    return FAITH_SET[v] === true;
}
export function isPractice(v) {
    return PRACTICE_SET[v] === true;
}
export function isMadhab(v) {
    return typeof v === 'string' && MADHABS.indexOf(v) >= 0;
}
export function isFoodRule(v) {
    return RULE_SET[v] === true;
}
export function isKitchen(v) {
    return KITCHEN_SET[v] === true;
}
export function isDaySet(v) {
    return DAY_SET_SET[v] === true;
}
/**
 * Canonieke vorm van een lijst codes: alleen bekende waarden, zonder dubbelen, in de volgorde van
 * de woordenlijst (zodat gelijke profielen gelijk vergelijken). Onbekende codes (bijvoorbeeld van
 * een nieuwere versie van de app) vallen weg in plaats van te crashen.
 */
export function canonical(values, vocabulary) {
    if (!values)
        return [];
    const seen = {};
    for (const v of values)
        seen[v] = true;
    return vocabulary.filter((v) => seen[v] === true);
}
function toSet(values) {
    const out = {};
    for (const v of values)
        out[v] = true;
    return out;
}
//# sourceMappingURL=vocab.js.map